import React, { useState, useEffect, Fragment } from "react";
import { Link } from "react-router-dom";
import { Form, FloatingLabel, Card, Badge, Button } from "react-bootstrap";
import { Trash2, Edit3, Plus } from "react-feather";

import DataTable from "../../components/table";
import { useNotification } from "../../components/notification";
import { useConfirmation } from "../../contexts/confirmationContext";
import apiService from "../../services/apiService";
import { formatTime, formatAmount } from "../../utils";

const Discounts = () => {
  const { showNotification } = useNotification();
  const { showConfirmation } = useConfirmation();

  const [discounts, setDiscounts] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState({ PageNumber: 1, PageSize: 10 });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [loadingState, setLoadingState] = useState({ action: "", id: "" });

  // Fetch discounts on page load or when pagination/filter changes
  useEffect(() => {
    const fetchDiscounts = async () => {
      setIsLoading(true);
      try {
        const response = await apiService.get("/discounts", {
          page: page.PageNumber,
          limit: page.PageSize,
          isActive: selectedStatus,
        });

        setDiscounts(response.discounts); // Update discounts data
        setTotalRecords(response.pagination.total); // Update total records
      } catch (error) {
        showNotification("danger", error || "Failed to get discounts.", 5000);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDiscounts();
  }, [page.PageNumber, page.PageSize, selectedStatus, showNotification]);

  // Handle page change
  const handlePageChange = (pageNumber, pageSize) => {
    setPage({ PageNumber: pageNumber, PageSize: pageSize });
  };

  // Handle status filter change
  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setPage({ PageNumber: 1, PageSize: page.PageSize }); // Reset to the first page
  };

  // Handle delete discount
  const handleDeleteDiscount = async (discountId, discountName) => {
    showConfirmation({
      title: "Delete Discount",
      description: (
        <>
          Are you sure you want to delete the discount <b>"{discountName}"</b>?
          This action is permanent and cannot be undone.
        </>
      ),
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "primary",
      onConfirm: async () => {
        setLoadingState({ action: "delete", id: discountId });
        try {
          await apiService.delete(`/discounts/${discountId}`);
          setDiscounts((prev) =>
            prev.filter((discount) => discount.id !== discountId)
          );
          showNotification("success", "Discount deleted successfully!");
        } catch (error) {
          showNotification("danger", error || "Failed to delete discount.");
        } finally {
          setLoadingState(null);
        }
      },
    });
  };

  // Define columns for the DataTable
  const columns = [
    { accessorKey: "name", header: "Name" },
    {
      accessorKey: "code",
      header: "Code",
      cell: (info) => (
        <div className="d-flex align-items-center">
          <span>{info.getValue()}</span>
          <Button
            variant="link"
            size="sm"
            title="Copy Code"
            onClick={() => {
              navigator.clipboard.writeText(info.getValue());
              showNotification("success", "Code copied to clipboard!");
            }}
            className="p-0 ms-2"
          >
            <i className="far fa-copy"></i>
          </Button>
        </div>
      ),
    },
    { accessorKey: "discountScope", header: "Discount Scope" },
    { accessorKey: "applyType", header: "Apply Type" },
    { accessorKey: "scope", header: "Product Scope" },
    { accessorKey: "type", header: "Discount Type" },
    {
      accessorKey: "value",
      header: "Value",
      cell: (info) => (
        <span>
          {info.row.original.type === "percentage" ? (
            `${parseFloat(info.getValue())}%`
          ) : (
            <Fragment>
              <span className="currency">&#8377;</span>
              {formatAmount(info.getValue())}
            </Fragment>
          )}
        </span>
      ),
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: (info) => (
        <Badge bg={info.getValue() ? "success" : "danger"}>
          {info.getValue() ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      accessorKey: "startDate",
      header: "Start Date",
      cell: (info) => {
        const date = new Date(info.getValue());
        return formatTime(date, "short");
      },
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      cell: (info) => {
        const date = new Date(info.getValue());
        return formatTime(date, "short");
      },
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (info) => (
        <div>
          <Link
            to={`/discounts/edit/${info.row.original.id}`}
            className="btn btn-primary btn-sm me-1"
            title="Edit Discount"
          >
            <Edit3 size={16} />
          </Link>
          <Button
            variant="danger"
            size="sm"
            title="Delete Discount"
            onClick={() =>
              handleDeleteDiscount(info.row.original.id, info.row.original.name)
            }
            disabled={
              loadingState?.action === "delete" &&
              loadingState.id === info.row.original.id
            }
          >
            {loadingState?.action === "delete" &&
            loadingState.id === info.row.original.id ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              <Trash2 size={16} />
            )}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Card className="p-4 rounded-4">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h1 className="mb-2 fs-5">Manage Discounts</h1>
            <p className="mb-0 text-light-emphasis fw-light">
              View and manage discount details.
            </p>
          </div>

          <FloatingLabel controlId="ddStatus" label="Filter by Status">
            <Form.Select
              aria-label="Filter by Status"
              value={selectedStatus}
              onChange={handleStatusChange}
              style={{ width: "150px" }}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Form.Select>
          </FloatingLabel>
        </div>
        <hr className="my-4" />
        <div className="mb-3 mb-sm-0 text-end">
          <Button
            as={Link}
            to="/discounts/create"
            variant="primary"
            className="btn-main mb-3 mb-sm-0"
          >
            <Plus size={16} className="me-1 align-middle" />
            Add Discount
          </Button>
        </div>
        <DataTable
          columns={columns}
          data={discounts}
          totalRecords={totalRecords}
          page={page}
          onPageChange={handlePageChange}
          loadingState={isLoading}
          searchPlaceholder="Search discounts..."
        />
      </Card.Body>
    </Card>
  );
};

export default Discounts;
